import { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DataTable from "examples/Tables/DataTable";
import { Card } from "@mui/material";
import ArgonSelect from "components/ArgonSelect";
import ActivityReviewPanelModal from "../Components/ReviewPanels/ActivityReviewPanelModal";
import ChatReviewPanelModal from "../Components/ReviewPanels/ChatReviewPanelModal";
import ProfileReviewPanelModal from "../Components/ReviewPanels/ProfileReviewPanelModal";
import { getReports } from "services/reportService";
import CircularProgress from "@mui/material/CircularProgress";
import { ArrowBack } from "@mui/icons-material";
import ArgonButton from "components/ArgonButton";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  STATUS_FILTER,
  REPORT_REASON_FILTER,
  CONTENT_TYPE_FILTER,
} from "./consts";
import AvatarWithLoader from "../Components/Avatar/AvatarWithLoader";
import "./ManageModeratorById.css";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import SearchBar from "../Components/SearchBar";

function ManageModeratorById() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { moderator } = useLocation().state;
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReviewPanelModal, setOpenReviewPanelModal] = useState(false);
  const [reviewPanelType, setReviewPanelType] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER[0]);
  const [reportReasonFilter, setReportReasonFilter] = useState(
    REPORT_REASON_FILTER[0]
  );
  const [contentTypeFilter, setContentTypeFilter] = useState(
    CONTENT_TYPE_FILTER[0]
  );
  const { searchTerm, debouncedSearchTerm, handleSearchChange } =
    useDebouncedSearch(500);
  const handleCloseReviewPanelModal = () => {
    setOpenReviewPanelModal(false);
    setReviewPanelType(null);
    setSelectedReport(null);
  };

  useEffect(() => {
    setLoading(true);
    getReports(
      null,
      statusFilter.value !== "All" ? statusFilter.value : null,
      reportReasonFilter.value !== "All" ? reportReasonFilter.value : null,
      contentTypeFilter.value !== "All" ? contentTypeFilter.value : null,
      null,
      null,
      id,
      debouncedSearchTerm || null
    )
      .then((res) => {
        if (res.success && res.data && res.data.reports) {
          setFlaggedContent(res.data.reports);
        } else {
          setFlaggedContent([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching reports:", error);
        setFlaggedContent([]);
        setLoading(false);
      });
  }, [
    statusFilter,
    reportReasonFilter,
    contentTypeFilter,
    debouncedSearchTerm,
  ]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatContentType = (contentType) => {
    return contentType.charAt(0).toUpperCase() + contentType.slice(1);
  };

  const formatReason = (reason) => {
    return reason
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const columns = [
    {
      Header: "Reported User",
      accessor: "reportedAgainst",
      Cell: ({ row }) => (
        <ArgonBox display="flex" alignItems="center">
          <AvatarWithLoader
            src={row.original.reportedAgainst?.avatar}
            alt={row.original.reportedAgainst?.name}
          />
          <ArgonTypography className="flagged-content-table-text">
            {row.original.reportedAgainst?.name}
          </ArgonTypography>
        </ArgonBox>
      ),
    },
    {
      Header: "Content Type",
      accessor: "contentType",
      Cell: ({ row }) => (
        <ArgonTypography className="flagged-content-table-text">
          {formatContentType(row.original.contentType)}
        </ArgonTypography>
      ),
    },
    {
      Header: "Report Reason",
      accessor: "reason",
      Cell: ({ row }) => (
        <ArgonTypography className="flagged-content-table-text">
          {formatReason(row.original.reason)}
        </ArgonTypography>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => (
        <span
          className="flagged-content-table-text"
          style={{
            background: "#f6f7f8",
            borderRadius: 12,
            padding: "4px 16px",
          }}
        >
          {formatStatus(row.original.status)}
        </span>
      ),
    },
    {
      Header: "Date",
      accessor: "createdAt",
      Cell: ({ row }) => (
        <ArgonTypography className="flagged-content-table-text">
          {formatDate(row.original.createdAt)}
        </ArgonTypography>
      ),
    },
    {
      Header: "Action Taken",
      accessor: "actionTaken",
      Cell: ({ row }) => (
        <ArgonTypography className="flagged-content-table-text">
          {row.original.actionTaken}
        </ArgonTypography>
      ),
    },
    {
      Header: "Date and Time of Action",
      accessor: "actionTakenAt",
      Cell: ({ row }) => (
        <ArgonTypography className="flagged-content-table-text">
          {formatDateTime(row.original.actionTakenAt)}
        </ArgonTypography>
      ),
    },
    {
      Header: "Action",
      accessor: "view_details",
      Cell: ({ row }) => (
        <ArgonTypography
          className="flagged-content-table-text view-details-text"
          fontWeight="medium"
          onClick={() => {
            setSelectedReport(row.original);
            if (row.original.contentType === "activity") {
              setOpenReviewPanelModal(true);
              setReviewPanelType("activity");
            } else if (row.original.contentType === "activity_message") {
              setOpenReviewPanelModal(true);
              setReviewPanelType("activity_message");
            } else if (row.original.contentType === "user") {
              setOpenReviewPanelModal(true);
              setReviewPanelType("user");
            }
          }}
        >
          View Details
        </ArgonTypography>
      ),
    },
  ];

  return (
    <>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search Reported Users"
      />
      <ArgonBox>
        <ArgonButton
          color="white"
          onClick={() => navigate("/admin/manage-moderator")}
        >
          <ArrowBack sx={{ mr: 1 }} />
          <ArgonTypography variant="h5" fontWeight="medium">
            Back
          </ArgonTypography>
        </ArgonButton>
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
          p={2}
          sx={{
            minHeight: "120px",
            backgroundColor: "#fff",
            position: "relative",
            borderRadius: 4,
          }}
        >
          <ArgonBox display="flex" alignItems="center" gap={2}>
            <AvatarWithLoader src={moderator?.avatar} alt={moderator?.name} />
            <ArgonTypography variant="h5" fontWeight="medium">
              {moderator?.name}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
      <Card
        sx={{
          overflow: "visible",
          marginTop: 6,
          paddingBottom: flaggedContent.length > 0 ? 0 : 6,
        }}
      >
        <ArgonBox padding={3}>
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <ArgonTypography variant="h5" fontWeight="medium">
              Flagged Content
            </ArgonTypography>
            <ArgonBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              <ArgonSelect
                value={statusFilter}
                options={STATUS_FILTER}
                onChange={setStatusFilter}
                hasShadow
                width="90px"
              />
              <ArgonSelect
                value={reportReasonFilter}
                options={REPORT_REASON_FILTER}
                onChange={setReportReasonFilter}
                hasShadow
                width="140px"
              />
              <ArgonSelect
                value={contentTypeFilter}
                options={CONTENT_TYPE_FILTER}
                onChange={setContentTypeFilter}
                hasShadow
                width="130px"
              />
            </ArgonBox>
          </ArgonBox>
          {loading ? (
            <ArgonBox display="flex" justifyContent="center">
              <CircularProgress />
            </ArgonBox>
          ) : flaggedContent.length > 0 ? (
            <DataTable
              table={{ columns, rows: flaggedContent }}
              entriesPerPage={false}
              canSearch={false}
              showTotalEntries={false}
              pagination={false}
            />
          ) : (
            <ArgonTypography variant="h6" textAlign="center">
              No flagged content found
            </ArgonTypography>
          )}
          {openReviewPanelModal &&
            (reviewPanelType === "activity" ? (
              <ActivityReviewPanelModal
                open={openReviewPanelModal}
                handleClose={handleCloseReviewPanelModal}
                _id={selectedReport._id}
              />
            ) : reviewPanelType === "activity_message" ? (
              <ChatReviewPanelModal
                open={openReviewPanelModal}
                handleClose={handleCloseReviewPanelModal}
                _id={selectedReport._id}
              />
            ) : (
              <ProfileReviewPanelModal
                open={openReviewPanelModal}
                handleClose={handleCloseReviewPanelModal}
                _id={selectedReport._id}
              />
            ))}
        </ArgonBox>
      </Card>
    </>
  );
}

export default ManageModeratorById;
